/**
 * @fileoverview Custom sending-domain manager (MOR-529, workspace settings).
 *
 * Full in-app domain lifecycle over the Inkbox REST API — no console
 * round-trip: add a domain (with apex-MX confirmation), publish the issued
 * DNS records (copy controls per record), watch verification progress
 * (auto-refresh while propagating + manual re-check), set as org default,
 * rotate DKIM, and remove/restore (24h grace). The DNS-records UX follows
 * the domain-sdk settings-page pattern: exact records with purpose, status,
 * and copy buttons.
 *
 * Registration/DKIM/deletion are admin-gated server-side; when the Inkbox
 * plan has no custom domains the API returns a plan gate rendered as an
 * upgrade card. Billing ($4/month per domain, by Inkbox) starts only once a
 * domain verifies.
 *
 * `provider="ses"` (MAIL_PROVIDER=ses, G6 MOR-2322) runs the same manager over
 * SES identities: the records table comes from `domainRecords` (DKIM, MAIL
 * FROM, SPF, inbound MX, a DMARC recommendation) with an apex-MX hint that
 * recommends a subdomain, a removal is immediate (nothing to restore), and
 * there is no billing or plan gate. The wording that differs lives in
 * `sendingDomains.helpers.ts`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import {
  AtSign,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Globe,
  Plus,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { suggestSubdomain } from "@cvx/integrations/ses/domainRecords";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import {
  SENDING_DOMAINS_COPY,
  SES_PURPOSE_LABELS,
  SES_RECORDS_NOTE,
  SES_STATUS_OVERRIDES,
  type SendingDomainsProvider,
  type StatusMeta,
} from "./sendingDomains.helpers";

/** Shared card chrome — mirrors the per-job settings sections. */
export function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof Globe;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card">
      <div className="flex items-start gap-3 border-b bg-muted/30 px-5 py-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background">
          <Icon className="size-4 text-primary" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="space-y-4 px-5 py-5">{children}</div>
    </section>
  );
}

interface DomainRow {
  id: string;
  domain: string;
  status: string;
  isDefault: boolean;
  verifiedAt: number | null;
}

interface DnsRecord {
  type: string;
  host: string;
  value: string;
  priority?: number;
  ttl?: number;
  purpose?: string;
  status?: string;
}

interface DomainDetail extends DomainRow {
  dnsRecords: DnsRecord[];
}

/** From `domainRecords` (ses): MX hosts at the domain that belong to another provider. */
interface ApexMx {
  hosts: string[];
  suggestedSubdomain: string | null;
}

const DOMAIN_STATUS_META: Record<string, StatusMeta> = {
  not_started: {
    label: "Not started",
    dot: "bg-slate-400",
    text: "text-slate-600 dark:text-slate-400",
    explain: "Publish the DNS records below to begin verification.",
  },
  awaiting_ownership: {
    label: "Awaiting ownership",
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-400",
    explain: "Waiting for the ownership TXT record to appear in DNS.",
  },
  pending: {
    label: "Propagating",
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-400",
    explain:
      "Ownership confirmed. Waiting for the remaining records to propagate.",
  },
  verifying: {
    label: "Verifying",
    dot: "bg-sky-500",
    text: "text-sky-700 dark:text-sky-400",
    explain: "Records found. Final checks are running.",
  },
  verified: {
    label: "Verified",
    dot: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-400",
    explain: "Healthy. This domain can send and receive mail.",
  },
  dns_invalid: {
    label: "DNS mismatch",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-400",
    explain:
      "Records were found but the values don't match what was issued. Compare each record below with your registrar and republish.",
  },
  failed: {
    label: "Failed",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-400",
    explain:
      "The verification window elapsed. Remove this domain and add it again.",
  },
  degraded: {
    label: "Degraded",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-400",
    explain:
      "A previously verified record was changed or removed at your registrar. Republish the records below.",
  },
  pending_dkim_rotation: {
    label: "DKIM rotating",
    dot: "bg-sky-500",
    text: "text-sky-700 dark:text-sky-400",
    explain:
      "A new DKIM key was issued. Publish the updated TXT record — the old key keeps sending until the new one verifies.",
  },
  pending_deletion: {
    label: "Removing",
    dot: "bg-slate-400",
    text: "text-slate-600 dark:text-slate-400",
    explain: "Scheduled for deletion. You can restore it within 24 hours.",
  },
};

function statusMeta(
  status: string,
  provider: SendingDomainsProvider = "inkbox",
): StatusMeta {
  const base = DOMAIN_STATUS_META[status] ?? {
    label: status.replace(/_/g, " "),
    dot: "bg-slate-400",
    text: "text-slate-600 dark:text-slate-400",
    explain: "",
  };
  return provider === "ses"
    ? { ...base, ...SES_STATUS_OVERRIDES[status] }
    : base;
}

/** Statuses that keep changing on their own — worth auto-refreshing. */
const POLLED_STATUSES = new Set([
  "not_started",
  "awaiting_ownership",
  "pending",
  "verifying",
  "dns_invalid",
  "degraded",
  "pending_dkim_rotation",
  "temporary_failure",
]);

const INKBOX_BILLING_URL =
  "https://inkbox.ai/console/organizations?tab=billing";

function StatusChip({
  status,
  provider,
}: {
  status: string;
  provider: SendingDomainsProvider;
}) {
  const meta = statusMeta(status, provider);
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-[11px] font-medium leading-none",
        meta.text,
      )}
    >
      <span className={cn("size-1.5 rounded-full", meta.dot)} />
      {meta.label}
    </span>
  );
}

/** Copy-to-clipboard button with a brief confirmation tick. */
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? (
        <Check className="size-3.5 text-emerald-600" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </button>
  );
}

/** Exact records to publish, with per-field copy controls. */
function DnsRecordsTable({
  records,
  provider,
}: {
  records: DnsRecord[];
  provider: SendingDomainsProvider;
}) {
  const hasLongTxt = records.some(
    (r) => r.type === "TXT" && r.value.length > 255,
  );
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Host</th>
              <th className="px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr
                key={`${r.type}:${r.host}:${i}`}
                className="border-b last:border-b-0"
              >
                <td className="px-3 py-2 align-top">
                  <span className="font-mono font-medium text-foreground">
                    {r.type}
                  </span>
                  {r.purpose ? (
                    <p className="mt-0.5 text-[10px] capitalize text-muted-foreground">
                      {(provider === "ses"
                        ? SES_PURPOSE_LABELS[r.purpose]
                        : undefined) ?? r.purpose.replace(/_/g, " ")}
                    </p>
                  ) : null}
                </td>
                <td className="max-w-[180px] px-3 py-2 align-top">
                  <div className="flex items-start gap-1">
                    <span className="break-all font-mono text-foreground">
                      {r.host}
                    </span>
                    <CopyButton value={r.host} label="host" />
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex items-start gap-1">
                    <span className="line-clamp-3 max-w-[360px] break-all font-mono text-foreground">
                      {r.value}
                    </span>
                    <CopyButton value={r.value} label="value" />
                  </div>
                  {typeof r.priority === "number" ? (
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      Priority {r.priority}
                    </p>
                  ) : null}
                  {r.status &&
                  r.status !== "valid" &&
                  r.status !== "unknown" ? (
                    <p
                      className={cn(
                        "mt-0.5 text-[10px] capitalize",
                        r.status === "invalid"
                          ? "text-red-600 dark:text-red-400"
                          : "text-amber-700 dark:text-amber-400",
                      )}
                    >
                      {r.status.replace(/_/g, " ")}
                    </p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Add each record at your DNS provider exactly as shown, then leave TTL at
        its default.
        {hasLongTxt
          ? " The DKIM value is longer than 255 characters — most registrars accept it as one value; if yours truncates it, split it into 255-character quoted chunks separated by single spaces."
          : ""}
      </p>
      {provider === "ses" ? (
        <p className="text-[11px] text-muted-foreground">{SES_RECORDS_NOTE}</p>
      ) : null}
    </div>
  );
}

export function SendingDomainsSection({
  orgId,
  provider = "inkbox",
}: {
  orgId: Id<"organizations">;
  provider?: SendingDomainsProvider;
}) {
  const isSes = provider === "ses";
  const copy = SENDING_DOMAINS_COPY[provider];
  const listDomains = useAction(
    api.integrations.inkbox.actions.listSendingDomains,
  );
  const setDefaultDomain = useAction(
    api.integrations.inkbox.actions.setDefaultSendingDomain,
  );
  const registerDomain = useAction(
    api.integrations.inkbox.actions.registerSendingDomain,
  );
  const getDomain = useAction(api.integrations.inkbox.actions.getSendingDomain);
  const checkVerification = useAction(
    api.integrations.inkbox.actions.checkSendingDomainVerification,
  );
  const rotateDkim = useAction(
    api.integrations.inkbox.actions.rotateSendingDomainDkim,
  );
  const deleteDomain = useAction(
    api.integrations.inkbox.actions.deleteSendingDomain,
  );
  const restoreDomain = useAction(
    api.integrations.inkbox.actions.restoreSendingDomain,
  );
  const getDomainRecords = useAction(
    api.integrations.ses.domains.domainRecords,
  );

  const [data, setData] = useState<{
    platformDomain: string;
    domains: DomainRow[];
  } | null>(null);
  const [details, setDetails] = useState<Record<string, DomainDetail>>({});
  const [apexMx, setApexMx] = useState<Record<string, ApexMx>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Key of the in-flight row action (e.g. "verify:<id>") — serializes them. */
  const [busy, setBusy] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [planGate, setPlanGate] = useState<{
    message: string;
    upgradeUrl: string;
  } | null>(null);
  const [apexWarning, setApexWarning] = useState<{
    domain: string;
    message: string;
  } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<DomainRow | null>(null);
  const [confirmRotate, setConfirmRotate] = useState<DomainRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await listDomains({ orgId }));
    } catch (err) {
      setLoadError(extractErrorMessage(err, "Could not load domains"));
    } finally {
      setLoading(false);
    }
  }, [listDomains, orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const storeDetail = useCallback((domain: DomainDetail) => {
    if (!domain.id) return;
    setDetails((prev) => ({ ...prev, [domain.id]: domain }));
    setData((prev) =>
      prev
        ? {
            ...prev,
            domains: prev.domains.map((d) =>
              d.id === domain.id
                ? {
                    ...d,
                    status: domain.status || d.status,
                    isDefault: domain.isDefault,
                    verifiedAt: domain.verifiedAt ?? d.verifiedAt,
                  }
                : d,
            ),
          }
        : prev,
    );
  }, []);

  const fetchDetail = useCallback(
    async (domainId: string, opts?: { silent?: boolean }) => {
      try {
        // On ses the records come from `domainRecords`, which adds the
        // apex-MX check; the detail it carries is the same shape.
        const result = isSes
          ? await getDomainRecords({ orgId, domain: domainId })
          : await getDomain({ orgId, domainId });
        if (result.ok) {
          storeDetail(result.domain);
          if ("apexMx" in result) {
            setApexMx((prev) => ({
              ...prev,
              [result.domain.id]: result.apexMx,
            }));
          }
        } else if (!opts?.silent) {
          toast.error(result.message);
        }
      } catch (err) {
        if (!opts?.silent) {
          toast.error(extractErrorMessage(err, "Could not load the domain"));
        }
      }
    },
    [getDomain, getDomainRecords, isSes, orgId, storeDetail],
  );

  // Auto-refresh the expanded domain while its records propagate.
  const expandedStatus = expandedId
    ? (details[expandedId]?.status ??
      data?.domains.find((d) => d.id === expandedId)?.status)
    : undefined;
  const pollRef = useRef<{ id: string; status: string } | null>(null);
  useEffect(() => {
    pollRef.current =
      expandedId && expandedStatus
        ? { id: expandedId, status: expandedStatus }
        : null;
    if (
      !expandedId ||
      !expandedStatus ||
      !POLLED_STATUSES.has(expandedStatus)
    ) {
      return;
    }
    const interval = setInterval(() => {
      const current = pollRef.current;
      if (current) void fetchDetail(current.id, { silent: true });
    }, 20_000);
    return () => clearInterval(interval);
  }, [expandedId, expandedStatus, fetchDetail]);

  const toggleExpand = (row: DomainRow) => {
    const next = expandedId === row.id ? null : row.id;
    setExpandedId(next);
    if (next && !details[next]) void fetchDetail(next);
  };

  const submitAdd = async (domain: string, apexAcknowledged: boolean) => {
    setAdding(true);
    try {
      const result = await registerDomain({
        orgId,
        domain,
        ...(apexAcknowledged ? { apexMxAcknowledged: true } : {}),
      });
      if (result.ok) {
        toast.success(
          `${result.domain.domain} added — publish the DNS records to verify it`,
        );
        setAddValue("");
        setAddOpen(false);
        setApexWarning(null);
        storeDetail(result.domain);
        setExpandedId(result.domain.id || null);
        await refresh();
        if (result.domain.id) storeDetail(result.domain);
      } else if (result.code === "plan_upgrade_required") {
        setPlanGate({
          message: result.message,
          upgradeUrl: result.upgradeUrl ?? INKBOX_BILLING_URL,
        });
      } else if (result.code === "apex_mx_warning") {
        setApexWarning({ domain, message: result.message });
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to add the domain"));
    } finally {
      setAdding(false);
    }
  };

  const runRowAction = async (
    key: string,
    fn: () => Promise<void>,
  ): Promise<void> => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      toast.error(extractErrorMessage(err, "The domain action failed"));
    } finally {
      setBusy(null);
    }
  };

  const handleCheckNow = (row: DomainRow) =>
    runRowAction(`verify:${row.id}`, async () => {
      const result = await checkVerification({ orgId, domainId: row.id });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      storeDetail(result.domain);
      if (result.domain.status === "verified" && row.status !== "verified") {
        toast.success(`${row.domain} is verified and ready to send`);
      }
    });

  const handleSetDefault = (domainName: string) =>
    runRowAction(`default:${domainName}`, async () => {
      await setDefaultDomain({ orgId, domain: domainName });
      toast.success(
        domainName === data?.platformDomain
          ? "Reverted to the platform domain"
          : `New mailboxes will send from @${domainName}`,
      );
      await refresh();
    });

  const handleRotate = (row: DomainRow) =>
    runRowAction(`rotate:${row.id}`, async () => {
      const result = await rotateDkim({ orgId, domainId: row.id });
      setConfirmRotate(null);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      storeDetail(result.domain);
      setExpandedId(row.id);
      toast.success(copy.rotatedToast);
    });

  const handleRemove = (row: DomainRow) =>
    runRowAction(`remove:${row.id}`, async () => {
      const result = await deleteDomain({ orgId, domainId: row.id });
      setConfirmRemove(null);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success(`${row.domain} ${copy.removedToast}`);
      await refresh();
      await fetchDetail(row.id, { silent: true });
    });

  const handleRestore = (row: DomainRow) =>
    runRowAction(`restore:${row.id}`, async () => {
      const result = await restoreDomain({ orgId, domainId: row.id });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      storeDetail(result.domain);
      toast.success(`${row.domain} restored`);
      await refresh();
    });

  const hasCustomDefault = data?.domains.some((d) => d.isDefault) ?? false;
  const platformDomain =
    data?.platformDomain ?? (isSes ? "" : "inkboxmail.com");

  return (
    <SettingsSection
      icon={Globe}
      title="Sending domain"
      description={copy.description}
    >
      {loading && !data ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner className="text-muted-foreground" />
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-xs text-destructive">{loadError}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => void refresh()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          {/* Platform default row */}
          <div className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <AtSign className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate font-mono text-sm text-foreground">
                  {platformDomain || "Not configured"}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {isSes
                    ? "Mail domain — works out of the box."
                    : "Platform default — works out of the box."}
                </p>
              </div>
            </div>
            {!hasCustomDefault ? (
              <span className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-[11px] font-medium leading-none text-primary">
                In use
              </span>
            ) : platformDomain ? (
              // Nothing to revert to when the deployment has no mail domain.
              <Button
                size="sm"
                variant="outline"
                disabled={busy !== null}
                onClick={() => void handleSetDefault(platformDomain)}
              >
                {busy === `default:${platformDomain}` && (
                  <Spinner className="mr-1.5 size-3.5" />
                )}
                Revert to this
              </Button>
            ) : null}
          </div>

          {/* Custom domains */}
          {(data?.domains ?? []).map((row) => {
            const expanded = expandedId === row.id;
            const detail = details[row.id];
            const status = detail?.status ?? row.status;
            const meta = statusMeta(status, provider);
            const apex = apexMx[row.id];
            const pendingDeletion = status === "pending_deletion";
            return (
              <div key={row.id || row.domain} className="rounded-lg border">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  onClick={() => toggleExpand(row)}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Globe className="size-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm text-foreground">
                        {row.domain}
                      </p>
                      {row.verifiedAt ? (
                        <p className="text-[11px] text-muted-foreground">
                          First verified{" "}
                          {new Date(row.verifiedAt).toLocaleDateString()}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {row.isDefault ? (
                      <span className="rounded-md bg-primary/10 px-2 py-1 text-[11px] font-medium leading-none text-primary">
                        In use
                      </span>
                    ) : null}
                    <StatusChip status={status} provider={provider} />
                    <ChevronDown
                      className={cn(
                        "size-4 text-muted-foreground transition-transform",
                        expanded && "rotate-180",
                      )}
                    />
                  </div>
                </button>

                {expanded ? (
                  <div className="space-y-3 border-t bg-muted/20 px-4 py-4">
                    <p className={cn("text-xs", meta.text)}>{meta.explain}</p>

                    {apex && apex.hosts.length > 0 ? (
                      <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10">
                        <p className="text-xs text-amber-800 dark:text-amber-300">
                          {`${row.domain} already has MX records (${apex.hosts.join(", ")}). Publishing the inbound MX below would replace that provider. Consider a subdomain such as `}
                          <span className="font-mono">
                            {apex.suggestedSubdomain ??
                              suggestSubdomain(row.domain)}
                          </span>
                          {" instead."}
                        </p>
                      </div>
                    ) : null}

                    {detail ? (
                      detail.dnsRecords.length > 0 && !pendingDeletion ? (
                        <DnsRecordsTable
                          records={detail.dnsRecords}
                          provider={provider}
                        />
                      ) : null
                    ) : (
                      <div className="flex h-16 items-center justify-center">
                        <Spinner className="text-muted-foreground" />
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {pendingDeletion ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy !== null}
                          onClick={() => void handleRestore(row)}
                        >
                          {busy === `restore:${row.id}` && (
                            <Spinner className="mr-1.5 size-3.5" />
                          )}
                          Restore domain
                        </Button>
                      ) : (
                        <>
                          {status !== "verified" &&
                          (isSes || status !== "failed") ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy !== null}
                              onClick={() => void handleCheckNow(row)}
                            >
                              {busy === `verify:${row.id}` ? (
                                <Spinner className="mr-1.5 size-3.5" />
                              ) : (
                                <RefreshCw className="mr-1.5 size-3.5" />
                              )}
                              Check verification
                            </Button>
                          ) : null}
                          {status === "verified" && !row.isDefault ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy !== null}
                              onClick={() => void handleSetDefault(row.domain)}
                            >
                              {busy === `default:${row.domain}` && (
                                <Spinner className="mr-1.5 size-3.5" />
                              )}
                              Use this domain
                            </Button>
                          ) : null}
                          {status === "verified" || status === "degraded" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy !== null}
                              onClick={() => setConfirmRotate(row)}
                            >
                              Rotate DKIM
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            disabled={busy !== null}
                            onClick={() => setConfirmRemove(row)}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {/* Plan gate */}
          {planGate ? (
            <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                {planGate.message}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Button asChild size="sm" variant="outline">
                  <a
                    href={planGate.upgradeUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Upgrade in Inkbox
                    <ExternalLink className="ml-1.5 size-3.5" />
                  </a>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPlanGate(null)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ) : null}

          {/* Add domain */}
          {addOpen ? (
            <form
              className="space-y-2 rounded-lg border border-dashed px-4 py-3"
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = addValue.trim().toLowerCase();
                if (trimmed) void submitAdd(trimmed, false);
              }}
            >
              <div className="flex items-center gap-2">
                <Input
                  autoFocus
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  placeholder="mail.yourfirm.com.au"
                  className="h-8 font-mono text-sm"
                  disabled={adding}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={adding || addValue.trim().length === 0}
                >
                  {adding && <Spinner className="mr-1.5 size-3.5" />}
                  Add
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={adding}
                  onClick={() => {
                    setAddOpen(false);
                    setAddValue("");
                  }}
                >
                  Cancel
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {copy.addHint}
              </p>
            </form>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-3">
              <p className="text-xs text-muted-foreground">
                Send from your own domain. You'll get DNS records to publish at
                your registrar; verification usually completes within minutes.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setAddOpen(true)}
              >
                <Plus className="mr-1.5 size-3.5" />
                Add domain
              </Button>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground">
            {copy.footer}
            {isSes ? null : (
              <>
                {" "}
                Billing is managed in the{" "}
                <a
                  href={INKBOX_BILLING_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Inkbox console
                </a>
                .
              </>
            )}
          </p>
        </>
      )}

      {/* Apex MX confirmation */}
      <Dialog
        open={apexWarning !== null}
        onOpenChange={(open) => {
          if (!open) setApexWarning(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>This domain already receives email</DialogTitle>
            <DialogDescription>
              {apexWarning ? copy.apexDescription(apexWarning.domain) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={adding}
              onClick={() => {
                // On ses the recommendation is pre-filled for the admin.
                if (isSes && apexWarning) {
                  setAddValue(suggestSubdomain(apexWarning.domain));
                }
                setApexWarning(null);
              }}
            >
              Use a subdomain instead
            </Button>
            <Button
              variant="destructive"
              disabled={adding}
              onClick={() => {
                if (apexWarning) void submitAdd(apexWarning.domain, true);
              }}
            >
              {adding && <Spinner className="mr-1.5 size-3.5" />}
              {copy.apexConfirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog
        open={confirmRemove !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemove(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {confirmRemove?.domain}?</DialogTitle>
            <DialogDescription>{copy.removeDescription}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => setConfirmRemove(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy !== null}
              onClick={() => {
                if (confirmRemove) void handleRemove(confirmRemove);
              }}
            >
              {busy?.startsWith("remove:") && (
                <Spinner className="mr-1.5 size-3.5" />
              )}
              Remove domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate DKIM confirmation */}
      <Dialog
        open={confirmRotate !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRotate(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Rotate DKIM key for {confirmRotate?.domain}?
            </DialogTitle>
            <DialogDescription>{copy.rotateDescription}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy !== null}
              onClick={() => setConfirmRotate(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={busy !== null}
              onClick={() => {
                if (confirmRotate) void handleRotate(confirmRotate);
              }}
            >
              {busy?.startsWith("rotate:") && (
                <Spinner className="mr-1.5 size-3.5" />
              )}
              Rotate key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}
